/**
 * Componente para exibir conteúdo de imóveis (properties).
 *
 * Renderizado DENTRO do shell de Databases (DatabasesPage já provê o header com
 * breadcrumb + seletor de base + Configurações). Aqui vive a barra de ações do
 * conteúdo: busca (centralizada) + view-toggle + refresh + Cadastrar, seguida da
 * filterbar de chips (PropertiesFilterChipsBar) e das duas views (cards/tabela).
 * No mobile a view "Lista" degrada para cards (PropertyCard).
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { IoBedOutline, IoCarOutline } from 'react-icons/io5';
import { LuBath, LuRuler } from 'react-icons/lu';
import { DatabaseType, isDocumentIntegrationLinked } from '@troia-v3/types';
import type { DatabasePropertyData, DatabaseDocumentResponse, BusinessOpportunity } from '@troia-v3/types';
import {
  useDatabaseDocuments,
  useDeleteDatabaseDocument,
  useCopyDatabaseDocuments,
} from '../../hooks/useDatabaseDocuments';
import {
  Button,
  ListPageLoading,
  ListPageEmpty,
  ListPagePagination,
  PageToolbar,
  PlainHeadCell,
  SelectAllHeadCell,
  SelectRowCell,
  StatusPill,
  ToolbarSearch,
  ViewToggle,
  type StatusPillVariant,
} from '@/shared/components/ui';
import { CardActionsMenu, type CardAction } from '@/shared/components/ui/CardActionsMenu';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import { usePageSelection } from '@/shared/hooks';
import { extractApiFieldErrors } from '@/shared/utils/apiFieldErrors';
import { PropertiesFilterChipsBar } from './PropertiesFilterChipsBar';
import { PropertyCard, formatPrice } from './PropertyCard';
import { TransferDocumentsDialog, type TransferMode } from '../TransferDocumentsDialog';
import type { PropertiesFilters } from './PropertiesAdvancedFilters';
import { Icon } from '@/shared/icons';

// Type alias for property document response
type PropertyDocument = DatabaseDocumentResponse<DatabasePropertyData>;

// Status badge maps (mesmo padrão de ProductsContent para consistência entre bases)
const statusLabels: Record<string, string> = {
  available: 'Disponível',
  unavailable: 'Indisponível',
  under_construction: 'Em construção',
  launch: 'Lançamento',
  reserved: 'Reservado',
  archived: 'Arquivado'
};

/** Semântica de cor do status → StatusPill shared (F62). */
const statusVariants: Record<string, StatusPillVariant> = {
  available: 'success',
  unavailable: 'neutral',
  under_construction: 'warning',
  launch: 'success',
  reserved: 'info',
  archived: 'neutral'
};

/** Handlers do menu ••• — compartilhados entre card e linha. */
interface PropertyActionHandlers {
  onOpen: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  onCopy: (id: string) => void;
  onMove: (id: string) => void;
}

/**
 * Ações do menu ••• (F39: paridade com Documentos), agora com duplicar/copiar/mover.
 *
 * Recebe o REGISTRO inteiro, não só o id, porque o item "Mover" depende de o
 * registro ter vindo de integração (D1) — a regra é `isDocumentIntegrationLinked`
 * do pacote compartilhado, a MESMA que o backend usa para recusar. Duas cópias
 * divergem e a tela passa a oferecer o que a API recusa.
 */
const buildPropertyActions = (
  property: PropertyDocument,
  handlers: PropertyActionHandlers,
): CardAction[] => {
  const integrationLinked = isDocumentIntegrationLinked(property);
  return [
    { label: 'Abrir', icon: 'eye', onClick: () => handlers.onOpen(property.id) },
    { label: 'Editar', icon: 'edit', onClick: () => handlers.onEdit(property.id) },
    { label: 'Duplicar aqui', icon: 'copy', onClick: () => handlers.onDuplicate(property.id), dividerBefore: true },
    { label: 'Copiar para outra base', icon: 'folder-plus', onClick: () => handlers.onCopy(property.id) },
    {
      // O motivo entra no RÓTULO porque no celular o menu vira ActionSheet e o
      // `disabledReason` NÃO é repassado (CardActionsMenu.tsx:86-94 copia só
      // rótulo, ícone, cor e desabilitado). No computador o Tooltip continua
      // aparecendo pelo `disabledReason`.
      label: integrationLinked ? 'Mover (registro do ERP)' : 'Mover para outra base',
      icon: 'corner-up-right',
      onClick: () => handlers.onMove(property.id),
      disabled: integrationLinked,
      ...(integrationLinked
        ? { disabledReason: 'Registro veio de integração (ERP) e não pode ser movido. Copiar continua liberado.' }
        : {}),
    },
    { label: 'Excluir', icon: 'trash', onClick: () => handlers.onDelete(property.id), variant: 'danger', dividerBefore: true },
  ];
};

interface PropertiesContentProps {
  databaseId: string;
}

export const PropertiesContent: React.FC<PropertiesContentProps> = ({ databaseId }) => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [advancedFilters, setAdvancedFilters] = useState<PropertiesFilters>({});
  // Modal de copiar/mover: `null` = fechado. "Duplicar aqui" NÃO passa por aqui.
  const [transfer, setTransfer] = useState<{ mode: TransferMode; documentIds: string[] } | null>(null);

  const { data, isLoading, error, refetch } = useDatabaseDocuments<DatabasePropertyData>({
    databaseId,
    filters: {
      type: 'properties',
      search: searchTerm || undefined,
      // Mapear filtros avançados para a query
      ...(advancedFilters.opportunityType?.length && { opportunityType: advancedFilters.opportunityType[0] }),
      ...(advancedFilters.propertyType?.length && { propertyType: advancedFilters.propertyType }),
      ...(advancedFilters.itemStatus?.length && { itemStatus: advancedFilters.itemStatus[0] }),
      ...(advancedFilters.minPrice && { minPrice: advancedFilters.minPrice }),
      ...(advancedFilters.maxPrice && { maxPrice: advancedFilters.maxPrice }),
      // Filtros de características
      ...(advancedFilters.minBedrooms && { minBedrooms: advancedFilters.minBedrooms }),
      ...(advancedFilters.maxBedrooms && { maxBedrooms: advancedFilters.maxBedrooms }),
      ...(advancedFilters.minBathrooms && { minBathrooms: advancedFilters.minBathrooms }),
      ...(advancedFilters.maxBathrooms && { maxBathrooms: advancedFilters.maxBathrooms }),
      ...(advancedFilters.minParkingSpaces && { minParkingSpaces: advancedFilters.minParkingSpaces }),
      ...(advancedFilters.maxParkingSpaces && { maxParkingSpaces: advancedFilters.maxParkingSpaces }),
      ...(advancedFilters.minArea && { minArea: advancedFilters.minArea }),
      ...(advancedFilters.maxArea && { maxArea: advancedFilters.maxArea }),
      // Filtros de localização
      ...(advancedFilters.city && { city: advancedFilters.city }),
      ...(advancedFilters.neighborhood && { neighborhood: advancedFilters.neighborhood }),
    },
    page: currentPage,
    limit: 12,
  });

  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    setCurrentPage(1);
    void refetch();
  };

  const handleFiltersChange = (filters: PropertiesFilters) => {
    setAdvancedFilters(filters);
    setCurrentPage(1);
  };

  const goToNew = () => navigate(`/databases/${databaseId}/documents/new?type=properties`);
  const openDocument = (id: string) => navigate(`/databases/${databaseId}/documents/${id}`);
  const editDocument = (id: string) => navigate(`/databases/${databaseId}/documents/${id}/edit`);

  const deleteProperty = useDeleteDatabaseDocument();
  const copyProperties = useCopyDatabaseDocuments();

  const items = data?.items ?? [];

  // Seleção da PÁGINA (12 itens — D3: nada de "todos os N do filtro").
  // A chave de reinício limpa a seleção sozinha ao trocar de base, página,
  // visão, busca ou filtro — inclusive ao sair da Lista para Cards (D4).
  const selectionResetKey = `${databaseId}|${viewMode}|${currentPage}|${searchTerm}|${JSON.stringify(advancedFilters)}`;
  // Memoizado: array novo a cada render faria o hook recalcular à toa.
  const pageIds = useMemo(() => items.map((property) => property.id), [items]);
  const selection = usePageSelection({ pageIds, resetKey: selectionResetKey });

  const handleDelete = async (id: string) => {
    if (!(await confirm({ message: 'Excluir este imóvel?', title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' }))) return;
    deleteProperty.mutate(id);
    selection.deselect(id);
  };

  /**
   * "Duplicar aqui" = copiar com destino igual à base atual (D2). NÃO abre
   * modal. Em lote passa pela confirmação que a tela já usa, dizendo quantos
   * registros serão criados — copiar dispara as automações de "registro criado"
   * na base de destino, uma por registro (D9).
   */
  const duplicateHere = async (ids: string[]) => {
    if (ids.length === 0) return;
    if (ids.length > 1) {
      const confirmed = await confirm({
        message: `Duplicar ${ids.length} imóveis nesta mesma base? Serão criados ${ids.length} registros novos, e as automações de "registro criado" disparam para cada um. A referência (Ref) nasce em branco.`,
        title: 'Duplicar imóveis',
        confirmButtonText: 'Duplicar',
        variant: 'default',
      });
      if (!confirmed) return;
    }
    try {
      const result = await copyProperties.mutateAsync({
        sourceDatabaseId: databaseId,
        targetDatabaseId: databaseId,
        documentIds: ids,
      });
      if (result.summary.succeeded > 0) {
        toast.success(
          `${result.summary.succeeded} ${result.summary.succeeded === 1 ? 'imóvel duplicado' : 'imóveis duplicados'}`
        );
      }
      if (result.summary.rejected > 0) {
        toast.error(
          `${result.summary.rejected} ${result.summary.rejected === 1 ? 'não foi duplicado' : 'não foram duplicados'}`
        );
      }
      selection.clear();
    } catch (err) {
      // O request vai com `_handleErrorLocally` (toast global suprimido) — esta
      // é a única mensagem do erro de lote.
      toast.error(extractApiFieldErrors(err).message);
    }
  };

  const openTransfer = (mode: TransferMode, ids: string[]) => {
    if (ids.length === 0) return;
    setTransfer({ mode, documentIds: ids });
  };

  /** Quantos dos selecionados o backend aceitaria mover (D1). Zero = botão em massa desligado. */
  const selectedMovableCount = items.filter(
    (property) => selection.isSelected(property.id) && !isDocumentIntegrationLinked(property),
  ).length;

  /**
   * id → título a partir da página JÁ carregada: o modal escreve o nome de cada
   * registro recusado sem nenhuma busca extra.
   */
  const titleById = items.reduce<Record<string, string>>((acc, property) => {
    acc[property.id] = property.data?.title || 'Sem título';
    return acc;
  }, {});

  const cardHandlers: PropertyActionHandlers = {
    onOpen: openDocument,
    onEdit: editDocument,
    onDelete: handleDelete,
    onDuplicate: (id: string) => { void duplicateHere([id]); },
    onCopy: (id: string) => openTransfer('copy', [id]),
    onMove: (id: string) => openTransfer('move', [id]),
  };

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* ── Barra de ações do conteúdo (3 zonas · busca centralizada) ── */}
      <PageToolbar
        left={data?.total != null && (
            <span
              className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
              style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
            >
              {data.total} {data.total === 1 ? 'imóvel' : 'imóveis'}
            </span>
          )}
        center={
          <ToolbarSearch
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Buscar imóveis…"
            data-testid="properties-search"
          />
        }
        right={<>
          {/* Direita: view toggle + refresh + Cadastrar */}
          {/* View toggle (F09-11: shared, Lista primeiro, rótulo Cards) */}
          <ViewToggle
            modes={[
              { id: 'list', icon: 'list', label: 'Lista' },
              { id: 'grid', icon: 'grid', label: 'Cards' },
            ]}
            value={viewMode}
            onChange={setViewMode}
            data-testid="properties-view"
          />

          {/* Refresh (F07: casca 36×36 bordada + ícone 16 + hover ghost — A2 ganha hover) */}
          <button
            onClick={handleRefresh}
            className="flex items-center justify-center h-9 w-9 rounded-lg transition-colors flex-none"
            style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-muted-text)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-card-background)'; }}
            title="Atualizar dados"
            disabled={isLoading}
            data-testid="properties-refresh"
          >
            <Icon name="refresh" className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>

          {/* Cadastrar (único primário — Button shared, F01-03) */}
          <Button
            variant="primary"
            icon="plus"
            collapsibleLabel
            onClick={goToNew}
            data-testid="properties-new"
          >
            Cadastrar
          </Button>
        </>}
      />

      {/* ── Filterbar (chips + adicionar filtro) ── */}
      <PropertiesFilterChipsBar
        filters={advancedFilters}
        onFiltersChange={handleFiltersChange}
      />

      {/* ── Barra de seleção ──
          Guarda de F34/D4: só existe na visão Lista E no computador. O
          `hidden md:flex` casa com o breakpoint da PRÓPRIA tabela (que é
          `hidden md:block`) — no celular a Lista degrada para cards, que não
          têm quadradinho, então não há como haver seleção lá. */}
      {viewMode === 'list' && selection.selectedCount > 0 && (
        <div
          className="flex-none hidden md:flex items-center gap-2.5 px-4 py-2"
          style={{ backgroundColor: 'var(--theme-primary-background)', borderBottom: '1px solid var(--theme-header-border)' }}
          data-testid="properties-bulk-bar"
        >
          <span className="font-bold text-sm" style={{ color: 'var(--theme-primary)' }}>
            {selection.selectedCount} selecionado(s)
          </span>
          <span className="flex-1" />
          <button
            onClick={() => { void duplicateHere(Array.from(selection.selectedIds)); }}
            className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold"
            style={{ backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-primary)' }}
            data-testid="properties-bulk-duplicate"
          >
            <Icon name="copy" className="w-3.5 h-3.5" /> Duplicar aqui
          </button>
          <button
            onClick={() => openTransfer('copy', Array.from(selection.selectedIds))}
            className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold"
            style={{ backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-primary)' }}
            data-testid="properties-bulk-copy"
          >
            <Icon name="folder-plus" className="w-3.5 h-3.5" /> Copiar para…
          </button>
          <button
            onClick={() => openTransfer('move', Array.from(selection.selectedIds))}
            disabled={selectedMovableCount === 0}
            className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-primary)' }}
            data-testid="properties-bulk-move"
          >
            <Icon name="corner-up-right" className="w-3.5 h-3.5" />
            {selectedMovableCount === 0 ? 'Mover (registros do ERP)' : 'Mover para…'}
          </button>
          <button
            onClick={selection.clear}
            className="text-sm font-semibold"
            style={{ color: 'var(--theme-primary)' }}
            data-testid="properties-bulk-cancel"
          >
            Cancelar
          </button>
        </div>
      )}

      {/* ── Conteúdo ── */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {error && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--theme-error-subtle)', border: '1px solid var(--theme-error-color)' }}>
              <p style={{ color: 'var(--theme-error-fg)' }} data-testid="properties-error">
                Erro ao carregar imóveis: {error.message}
              </p>
            </div>
          )}

          {isLoading && <ListPageLoading message="Carregando imóveis..." />}

          {!isLoading && !error && items.length === 0 && (
            <ListPageEmpty
              icon="home"
              message="Nenhum imóvel encontrado"
              description={searchTerm ? 'Tente ajustar a busca ou os filtros.' : 'Não há imóveis cadastrados neste banco de dados'}
              actionLabel="Cadastrar imóvel"
              onAction={goToNew}
            />
          )}

          {/* Grid (Cards) — kebab ••• (F39: paridade com Documentos) */}
          {!isLoading && !error && items.length > 0 && viewMode === 'grid' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {items.map((property) => (
                <PropertyCard
                  key={property.id}
                  property={property}
                  onClick={() => openDocument(property.id)}
                  actions={buildPropertyActions(property, cardHandlers)}
                />
              ))}
            </div>
          )}

          {/* Lista (Tabela) — vira cards no mobile */}
          {!isLoading && !error && items.length > 0 && viewMode === 'list' && (
            <>
              {/* Mobile: cards */}
              <div className="grid grid-cols-1 gap-4 md:hidden">
                {items.map((property) => (
                  <PropertyCard
                    key={property.id}
                    property={property}
                    onClick={() => openDocument(property.id)}
                    actions={buildPropertyActions(property, cardHandlers)}
                  />
                ))}
              </div>

              {/* Desktop: tabela (F43/F46: head shared, densidade px-4 py-3) */}
              <div className="hidden md:block rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                <table className="w-full border-collapse">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                      <SelectAllHeadCell
                        checked={selection.allOnPageSelected}
                        indeterminate={selection.someOnPageSelected && !selection.allOnPageSelected}
                        onToggle={selection.toggleAllOnPage}
                        data-testid="properties-select-all"
                      />
                      <PlainHeadCell label="Imóvel" />
                      <PlainHeadCell label="Localização" />
                      <PlainHeadCell label="Características" />
                      <PlainHeadCell label="Preço" />
                      <PlainHeadCell label="Status" />
                      <PlainHeadCell className="w-14" />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((property) => (
                      <PropertyListItem
                        key={property.id}
                        property={property}
                        onClick={() => openDocument(property.id)}
                        selected={selection.isSelected(property.id)}
                        onToggleSelect={selection.toggle}
                        actions={buildPropertyActions(property, cardHandlers)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Paginação — "Mostrando X–Y de N" universal (F48) */}
          {!isLoading && !error && data && (
            <ListPagePagination
              currentPage={data.page}
              totalPages={data.totalPages}
              onPageChange={setCurrentPage}
              totalItems={data.total}
              pageSize={12}
              itemLabel="imóveis"
            />
          )}
        </div>
      </div>

      {/* Modal de copiar/mover — "Duplicar aqui" NÃO passa por aqui. */}
      <TransferDocumentsDialog
        isOpen={transfer !== null}
        onClose={() => setTransfer(null)}
        mode={transfer?.mode ?? 'copy'}
        sourceDatabaseId={databaseId}
        databaseType={DatabaseType.PROPERTIES}
        documentIds={transfer?.documentIds ?? []}
        titleById={titleById}
        onTransferred={() => selection.clear()}
      />
    </div>
  );
};

interface PropertyListItemProps {
  property: PropertyDocument;
  onClick?: () => void;
  /** Linha marcada no quadradinho (pinta o fundo e desliga o hover). */
  selected: boolean;
  onToggleSelect: (id: string) => void;
  /** Ações do menu ••• da linha — montadas pelo pai (F39). */
  actions: CardAction[];
}

/**
 * Item de lista de imóvel (Table View)
 */
const PropertyListItem: React.FC<PropertyListItemProps> = ({ property, onClick, selected, onToggleSelect, actions }) => {
  // Acessar dados dentro de property.data
  const propertyData = property.data;
  const firstPhoto = propertyData?.media?.photos?.[0];
  const saleOpportunity = propertyData?.opportunities?.find((opp: BusinessOpportunity) => opp.type === 'sale');
  const rentOpportunity = propertyData?.opportunities?.find((opp: BusinessOpportunity) => opp.type === 'rent');

  // Características
  const bedrooms = propertyData?.features?.bedrooms;
  const bathrooms = propertyData?.features?.bathrooms;
  const parkingSpaces = propertyData?.features?.parkingSpaces;
  const area = propertyData?.features?.totalArea || propertyData?.features?.privateArea;

  return (
    <tr
      className="border-b transition-colors cursor-pointer"
      style={{ borderColor: 'var(--theme-card-border)', backgroundColor: selected ? 'var(--theme-primary-background)' : undefined }}
      onClick={onClick}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent'; }}
      data-testid={`property-row-${property.id}`}
    >
      {/* Seleção — a célula para o clique nela mesma (senão marcar abre o registro) */}
      <SelectRowCell
        checked={selected}
        onToggle={() => onToggleSelect(property.id)}
        data-testid={`property-row-check-${property.id}`}
      />

      {/* Imóvel: thumbnail + título + referência */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          {firstPhoto ? (
            <img
              src={firstPhoto}
              alt={propertyData?.title || 'Imóvel'}
              className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
            />
          ) : (
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: 'var(--theme-surface-sunken)' }}
            >
              <Icon name="home" className="w-5 h-5" style={{ color: '#64748b' }} />
            </div>
          )}
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
              {propertyData?.title || 'Sem título'}
            </div>
            {propertyData?.reference && (
              <div className="text-xs mt-0.5 truncate" style={{ color: 'var(--theme-muted-text)' }}>
                Ref: {propertyData.reference}
              </div>
            )}
          </div>
        </div>
      </td>

      {/* Localização */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          <Icon name="map-pin" className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--theme-muted-text)' }} />
          <div className="text-sm" style={{ color: 'var(--theme-card-text)' }}>
            {propertyData?.address?.neighborhood && <span>{propertyData.address.neighborhood}, </span>}
            <span style={{ color: 'var(--theme-muted-text)' }}>
              {propertyData?.address?.city || 'N/A'}
            </span>
          </div>
        </div>
      </td>

      {/* Características */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-4 text-sm" style={{ color: 'var(--theme-card-text)' }}>
          {bedrooms !== undefined && (
            <div className="flex items-center gap-1" title="Quartos">
              <IoBedOutline className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
              <span>{bedrooms}</span>
            </div>
          )}
          {bathrooms !== undefined && (
            <div className="flex items-center gap-1" title="Banheiros">
              <LuBath className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
              <span>{bathrooms}</span>
            </div>
          )}
          {parkingSpaces !== undefined && (
            <div className="flex items-center gap-1" title="Vagas">
              <IoCarOutline className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
              <span>{parkingSpaces}</span>
            </div>
          )}
          {area !== undefined && (
            <div className="flex items-center gap-1" title="Área">
              <LuRuler className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
              <span>{area}m²</span>
            </div>
          )}
        </div>
      </td>

      {/* Preço */}
      <td className="px-4 py-3">
        <div className="flex flex-col gap-1 items-start">
          {saleOpportunity?.pricing?.amount && (
            <span
              className="inline-flex px-2 py-0.5 rounded text-xs font-semibold"
              style={{
                backgroundColor: 'var(--theme-success-background)',
                color: 'var(--theme-success-color)',
              }}
            >
              {formatPrice(saleOpportunity.pricing.amount)}
            </span>
          )}
          {rentOpportunity?.pricing?.amount && (
            <span
              className="inline-flex px-2 py-0.5 rounded text-xs font-semibold"
              style={{
                backgroundColor: 'var(--theme-primary-background)',
                color: 'var(--theme-primary)',
              }}
            >
              {formatPrice(rentOpportunity.pricing.amount)}/mês
            </span>
          )}
        </div>
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        {propertyData?.itemStatus && (
          <StatusPill
            variant={statusVariants[propertyData.itemStatus] ?? 'neutral'}
            label={statusLabels[propertyData.itemStatus] || propertyData.itemStatus}
            data-testid={`property-row-status-${property.id}`}
          />
        )}
      </td>

      {/* Ações — o `stopPropagation` no td impede o clique de abrir o registro */}
      <td className="px-4 py-3 text-right w-14" onClick={(e) => e.stopPropagation()}>
        <CardActionsMenu actions={actions} size="sm" data-testid={`property-row-actions-${property.id}`} />
      </td>
    </tr>
  );
};

PropertyListItem.displayName = 'PropertyListItem';
